import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControlLabel,
  Grid,
  IconButton,
  InputAdornment,
  MenuItem,
  Paper,
  Snackbar,
  Stack,
  Switch,
  Tab,
  Tabs,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SaveIcon from "@mui/icons-material/Save";
import SettingsIcon from "@mui/icons-material/Settings";
import AccountTreeIcon from "@mui/icons-material/AccountTree";
import MessageIcon from "@mui/icons-material/Message";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";

import ApiServices from "../../../../Utils/ApiServices/ApiServices";
import { getDecryptedAccessToken } from "../../../../Utils/LoginUserDetails.js/LoginUserSavedDetails";

const emptyServiceConfig = {
  provider: "gupshup",
  agent_id: "",
  sender_id: "",
  api_key: "",
  api_secret: "",
  webhook_verify_token: "",
  default_language: "en",
  metadataText: JSON.stringify({ region: "IN" }, null, 2),
  is_active: true,
};

const emptyFlowForm = {
  id: null,
  flow_id: "",
  flow_name: "",
  entry_point: "",
  is_active: true,
  stepsText: JSON.stringify(
    {
      WELCOME: {
        type: "rich_card",
        message: {
          title: "Welcome",
          description: "How can we help you?",
        },
      },
    },
    null,
    2
  ),
};

const emptyMessageForm = {
  id: null,
  title: "",
  message_type: "offer",
  body: "",
  tagsText: "",
  payloadText: JSON.stringify(
    {
      cta: {
        text: "Open",
        action: "OPEN_MENU",
      },
    },
    null,
    2
  ),
  is_active: true,
};

const providerOptions = ["gupshup", "google", "infobip", "kaleyra", "other"];
const messageTypeOptions = ["offer", "event", "custom"];

const ClientRcsManagement = () => {
  const clientId = sessionStorage.getItem("activeClientId");
  const token = getDecryptedAccessToken();
  const baseUrl = useMemo(() => "/api/v1", []);

  const [tab, setTab] = useState(0);

  const [serviceExists, setServiceExists] = useState(false);
  const [serviceConfig, setServiceConfig] = useState(emptyServiceConfig);
  const [loadingService, setLoadingService] = useState(false);
  const [savingService, setSavingService] = useState(false);

  const [flows, setFlows] = useState([]);
  const [loadingFlows, setLoadingFlows] = useState(false);
  const [savingFlow, setSavingFlow] = useState(false);
  const [flowDialogOpen, setFlowDialogOpen] = useState(false);
  const [flowForm, setFlowForm] = useState(emptyFlowForm);

  const [messages, setMessages] = useState([]);
  const [loadingMessages, setLoadingMessages] = useState(false);
  const [savingMessage, setSavingMessage] = useState(false);
  const [messageDialogOpen, setMessageDialogOpen] = useState(false);
  const [messageForm, setMessageForm] = useState(emptyMessageForm);

  const [errors, setErrors] = useState({
    service: {},
    flow: {},
    message: {},
  });

  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    severity: "success",
  });

  const headers = {
    Authorization: `Bearer ${token}`,
    "Content-Type": "application/json",
  };

  const showSnackbar = (message, severity = "success") => {
    setSnackbar({
      open: true,
      message,
      severity,
    });
  };

  const closeSnackbar = () => {
    setSnackbar((prev) => ({ ...prev, open: false }));
  };

  const parseJsonSafely = (value, fallback = {}) => {
    try {
      return { ok: true, value: JSON.parse(value) };
    } catch (error) {
      return { ok: false, value: fallback, error: "Invalid JSON format." };
    }
  };

  const formatJson = (value, fallback = {}) => {
    try {
      if (typeof value === "string") return JSON.stringify(JSON.parse(value), null, 2);
      return JSON.stringify(value || fallback, null, 2);
    } catch (error) {
      return JSON.stringify(fallback, null, 2);
    }
  };

  const normalizeTags = (value) =>
    value
      .split(",")
      .map((item) => item.trim())
      .filter(Boolean);

  const copyToClipboard = async (text, label) => {
    try {
      await navigator.clipboard.writeText(text || "");
      showSnackbar(`${label} copied successfully.`, "success");
    } catch (error) {
      showSnackbar(`Failed to copy ${label}.`, "error");
    }
  };

  const validateServiceConfig = () => {
    const nextErrors = {};

    if (!serviceConfig.provider?.trim()) nextErrors.provider = "Provider is required.";
    if (!serviceConfig.sender_id?.trim()) nextErrors.sender_id = "Sender ID is required.";
    if (!serviceConfig.default_language?.trim()) {
      nextErrors.default_language = "Default language is required.";
    }

    const metadataCheck = parseJsonSafely(serviceConfig.metadataText, {});
    if (!metadataCheck.ok) nextErrors.metadataText = metadataCheck.error;

    setErrors((prev) => ({ ...prev, service: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const validateFlow = () => {
    const nextErrors = {};

    if (!flowForm.flow_id?.trim()) nextErrors.flow_id = "Flow ID is required.";
    if (!flowForm.flow_name?.trim()) nextErrors.flow_name = "Flow name is required.";
    if (!flowForm.entry_point?.trim()) nextErrors.entry_point = "Entry point is required.";

    const stepsCheck = parseJsonSafely(flowForm.stepsText, {});
    if (!stepsCheck.ok) nextErrors.stepsText = stepsCheck.error;

    setErrors((prev) => ({ ...prev, flow: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const validateMessage = () => {
    const nextErrors = {};

    if (!messageForm.title?.trim()) nextErrors.title = "Title is required.";
    if (!messageForm.message_type?.trim()) nextErrors.message_type = "Message type is required.";
    if (!messageForm.body?.trim()) nextErrors.body = "Body is required.";

    const payloadCheck = parseJsonSafely(messageForm.payloadText, {});
    if (!payloadCheck.ok) nextErrors.payloadText = payloadCheck.error;

    setErrors((prev) => ({ ...prev, message: nextErrors }));
    return Object.keys(nextErrors).length === 0;
  };

  const fetchServiceConfig = async () => {
    if (!clientId) return;

    setLoadingService(true);
    try {
      const response = await ApiServices.get(
        `${baseUrl}/clients/${clientId}/rcs-service/`,
        headers
      );
      const data = response?.data || response || {};

      setServiceExists(true);
      setServiceConfig({
        provider: data.provider || "gupshup",
        agent_id: data.agent_id || "",
        sender_id: data.sender_id || "",
        api_key: data.api_key || "",
        api_secret: data.api_secret || "",
        webhook_verify_token: data.webhook_verify_token || "",
        default_language: data.default_language || "en",
        metadataText: formatJson(data.metadata, {}),
        is_active: Boolean(data.is_active),
      });
    } catch (error) {
      if (error?.response?.status === 404) {
        setServiceExists(false);
        setServiceConfig(emptyServiceConfig);
      } else {
        showSnackbar(
          error?.response?.data?.message || "Failed to fetch RCS service config.",
          "error"
        );
      }
    } finally {
      setLoadingService(false);
    }
  };

  const saveServiceConfig = async () => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    if (!validateServiceConfig()) return;

    const metadataCheck = parseJsonSafely(serviceConfig.metadataText, {});
    const payload = {
      provider: serviceConfig.provider,
      agent_id: serviceConfig.agent_id,
      sender_id: serviceConfig.sender_id,
      api_key: serviceConfig.api_key,
      api_secret: serviceConfig.api_secret,
      webhook_verify_token: serviceConfig.webhook_verify_token,
      default_language: serviceConfig.default_language,
      metadata: metadataCheck.value,
      is_active: serviceConfig.is_active,
    };

    setSavingService(true);
    try {
      if (serviceExists) {
        await ApiServices.put(
          `${baseUrl}/clients/${clientId}/rcs-service/`,
          payload,
          headers
        );
        showSnackbar("RCS service config updated successfully.", "success");
      } else {
        await ApiServices.post(
          `${baseUrl}/clients/${clientId}/rcs-service/`,
          payload,
          headers
        );
        setServiceExists(true);
        showSnackbar("RCS service config created successfully.", "success");
      }
      fetchServiceConfig();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to save RCS service config.",
        "error"
      );
    } finally {
      setSavingService(false);
    }
  };

  const deleteServiceConfig = async () => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    try {
      await ApiServices.delete(
        `${baseUrl}/clients/${clientId}/rcs-service/`,
        headers
      );
      setServiceExists(false);
      setServiceConfig(emptyServiceConfig);
      showSnackbar("RCS service config deleted successfully.", "success");
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to delete RCS service config.",
        "error"
      );
    }
  };

  const fetchFlows = async () => {
    if (!clientId) return;

    setLoadingFlows(true);
    try {
      const response = await ApiServices.get(`${baseUrl}/rcs/${clientId}/flows/`, headers);
      const data = response?.data || response || [];
      setFlows(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to fetch RCS flows.", "error");
    } finally {
      setLoadingFlows(false);
    }
  };

  const openCreateFlowDialog = () => {
    setFlowForm(emptyFlowForm);
    setErrors((prev) => ({ ...prev, flow: {} }));
    setFlowDialogOpen(true);
  };

  const openEditFlowDialog = (flow) => {
    setFlowForm({
      id: flow.id || null,
      flow_id: flow.flow_id || "",
      flow_name: flow.flow_name || "",
      entry_point: flow.entry_point || "",
      is_active: Boolean(flow.is_active),
      stepsText: formatJson(flow.steps, {}),
    });
    setErrors((prev) => ({ ...prev, flow: {} }));
    setFlowDialogOpen(true);
  };

  const saveFlow = async () => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    if (!validateFlow()) return;

    const stepsCheck = parseJsonSafely(flowForm.stepsText, {});
    const payload = {
      flow_id: flowForm.flow_id,
      flow_name: flowForm.flow_name,
      entry_point: flowForm.entry_point,
      is_active: flowForm.is_active,
      steps: stepsCheck.value,
    };

    setSavingFlow(true);
    try {
      if (flowForm.id) {
        await ApiServices.patch(
          `${baseUrl}/rcs/${clientId}/flows/${flowForm.flow_id}/`,
          payload,
          headers
        );
        showSnackbar("RCS flow updated successfully.", "success");
      } else {
        await ApiServices.post(`${baseUrl}/rcs/${clientId}/flows/`, payload, headers);
        showSnackbar("RCS flow created successfully.", "success");
      }
      setFlowDialogOpen(false);
      fetchFlows();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to save RCS flow.", "error");
    } finally {
      setSavingFlow(false);
    }
  };

  const deleteFlow = async (flowId) => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    try {
      await ApiServices.delete(`${baseUrl}/rcs/${clientId}/flows/${flowId}/`, headers);
      showSnackbar("RCS flow deleted successfully.", "success");
      fetchFlows();
    } catch (error) {
      showSnackbar(error?.response?.data?.message || "Failed to delete RCS flow.", "error");
    }
  };

  const fetchMessages = async () => {
    if (!clientId) return;

    setLoadingMessages(true);
    try {
      const response = await ApiServices.get(`${baseUrl}/rcs/${clientId}/messages/`, headers);
      const data = response?.data || response || [];
      setMessages(Array.isArray(data) ? data : data?.results || []);
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to fetch RCS custom messages.",
        "error"
      );
    } finally {
      setLoadingMessages(false);
    }
  };

  const openCreateMessageDialog = () => {
    setMessageForm(emptyMessageForm);
    setErrors((prev) => ({ ...prev, message: {} }));
    setMessageDialogOpen(true);
  };

  const openEditMessageDialog = (item) => {
    setMessageForm({
      id: item.id || null,
      title: item.title || "",
      message_type: item.message_type || "offer",
      body: item.body || "",
      tagsText: Array.isArray(item.tags) ? item.tags.join(", ") : "",
      payloadText: formatJson(item.payload, {}),
      is_active: Boolean(item.is_active),
    });
    setErrors((prev) => ({ ...prev, message: {} }));
    setMessageDialogOpen(true);
  };

  const saveMessage = async () => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    if (!validateMessage()) return;

    const payloadCheck = parseJsonSafely(messageForm.payloadText, {});
    const payload = {
      title: messageForm.title,
      message_type: messageForm.message_type,
      body: messageForm.body,
      payload: payloadCheck.value,
      tags: normalizeTags(messageForm.tagsText),
      is_active: messageForm.is_active,
    };

    setSavingMessage(true);
    try {
      if (messageForm.id) {
        await ApiServices.patch(
          `${baseUrl}/rcs/${clientId}/messages/${messageForm.id}/`,
          payload,
          headers
        );
        showSnackbar("RCS custom message updated successfully.", "success");
      } else {
        await ApiServices.post(`${baseUrl}/rcs/${clientId}/messages/`, payload, headers);
        showSnackbar("RCS custom message created successfully.", "success");
      }
      setMessageDialogOpen(false);
      fetchMessages();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to save RCS custom message.",
        "error"
      );
    } finally {
      setSavingMessage(false);
    }
  };

  const deleteMessage = async (messageId) => {
    if (!clientId) {
      showSnackbar("Active client ID not found.", "error");
      return;
    }

    try {
      await ApiServices.delete(
        `${baseUrl}/rcs/${clientId}/messages/${messageId}/`,
        headers
      );
      showSnackbar("RCS custom message deleted successfully.", "success");
      fetchMessages();
    } catch (error) {
      showSnackbar(
        error?.response?.data?.message || "Failed to delete RCS custom message.",
        "error"
      );
    }
  };

  useEffect(() => {
    if (!clientId) {
      showSnackbar("No active client selected.", "error");
      return;
    }

    fetchServiceConfig();
    fetchFlows();
    fetchMessages();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [clientId]);

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <Card
        sx={{
          borderRadius: 3,
          boxShadow: "0 8px 24px rgba(15, 23, 42, 0.08)",
          border: "1px solid rgba(148, 163, 184, 0.18)",
          mb: 3,
        }}
      >
        <CardContent>
          <Stack
            direction={{ xs: "column", md: "row" }}
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            spacing={2}
          >
            <Box>
              <Typography variant="h5" fontWeight={700}>
                RCS Management
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                Manage RCS service config, flows, and custom messages for the selected client.
              </Typography>
              <Stack direction="row" spacing={1} sx={{ mt: 1.5, flexWrap: "wrap" }}>
                <Chip label={`Client ID: ${clientId || "-"}`} color="primary" variant="outlined" />
                <Chip
                  label={serviceExists ? "Service Config Available" : "Service Config Not Found"}
                  color={serviceExists ? "success" : "default"}
                  variant="outlined"
                />
              </Stack>
            </Box>

            <Button
              variant="outlined"
              startIcon={<RefreshIcon />}
              onClick={() => {
                fetchServiceConfig();
                fetchFlows();
                fetchMessages();
              }}
            >
              Refresh All
            </Button>
          </Stack>

          <Tabs
            value={tab}
            onChange={(event, newValue) => setTab(newValue)}
            variant="scrollable"
            scrollButtons="auto"
            sx={{ mt: 3 }}
          >
            <Tab icon={<SettingsIcon />} iconPosition="start" label="Service Config" />
            <Tab icon={<AccountTreeIcon />} iconPosition="start" label="Flows" />
            <Tab icon={<MessageIcon />} iconPosition="start" label="Custom Messages" />
          </Tabs>
        </CardContent>
      </Card>

      {tab === 0 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  RCS Service Config
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Create, update, or delete per-client RCS provider configuration.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                {serviceExists && (
                  <Button
                    variant="outlined"
                    color="error"
                    startIcon={<DeleteIcon />}
                    onClick={deleteServiceConfig}
                  >
                    Delete
                  </Button>
                )}
                <Button
                  variant="contained"
                  startIcon={savingService ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
                  onClick={saveServiceConfig}
                  disabled={savingService || loadingService}
                >
                  {serviceExists ? "Update Config" : "Create Config"}
                </Button>
              </Stack>
            </Stack>

            {loadingService ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : (
              <Grid container spacing={2.5}>
                <Grid item xs={12} md={4}>
                  <TextField
                    select
                    fullWidth
                    label="Provider"
                    value={serviceConfig.provider}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, provider: e.target.value }))
                    }
                    error={Boolean(errors.service.provider)}
                    helperText={errors.service.provider}
                  >
                    {providerOptions.map((option) => (
                      <MenuItem key={option} value={option}>
                        {option}
                      </MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Agent ID"
                    value={serviceConfig.agent_id}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, agent_id: e.target.value }))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={4}>
                  <TextField
                    fullWidth
                    label="Sender ID"
                    value={serviceConfig.sender_id}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, sender_id: e.target.value }))
                    }
                    error={Boolean(errors.service.sender_id)}
                    helperText={errors.service.sender_id}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="API Key"
                    value={serviceConfig.api_key}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, api_key: e.target.value }))
                    }
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <Tooltip title="Copy API Key">
                            <IconButton
                              onClick={() => copyToClipboard(serviceConfig.api_key, "API Key")}
                            >
                              <ContentCopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        </InputAdornment>
                      ),
                    }}
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    type="password"
                    label="API Secret"
                    value={serviceConfig.api_secret}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, api_secret: e.target.value }))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={6}>
                  <TextField
                    fullWidth
                    label="Webhook Verify Token"
                    value={serviceConfig.webhook_verify_token}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({
                        ...prev,
                        webhook_verify_token: e.target.value,
                      }))
                    }
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <TextField
                    fullWidth
                    label="Default Language"
                    value={serviceConfig.default_language}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({
                        ...prev,
                        default_language: e.target.value,
                      }))
                    }
                    error={Boolean(errors.service.default_language)}
                    helperText={errors.service.default_language}
                  />
                </Grid>

                <Grid item xs={12} md={3}>
                  <Paper
                    variant="outlined"
                    sx={{
                      px: 2,
                      py: 1.5,
                      minHeight: 56,
                      display: "flex",
                      alignItems: "center",
                      borderRadius: 2,
                    }}
                  >
                    <FormControlLabel
                      control={
                        <Switch
                          checked={serviceConfig.is_active}
                          onChange={(e) =>
                            setServiceConfig((prev) => ({
                              ...prev,
                              is_active: e.target.checked,
                            }))
                          }
                        />
                      }
                      label={serviceConfig.is_active ? "Active" : "Inactive"}
                    />
                  </Paper>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={8}
                    label="Metadata (JSON)"
                    value={serviceConfig.metadataText}
                    onChange={(e) =>
                      setServiceConfig((prev) => ({ ...prev, metadataText: e.target.value }))
                    }
                    error={Boolean(errors.service.metadataText)}
                    helperText={errors.service.metadataText || 'Example: { "region": "IN" }'}
                  />
                </Grid>
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 1 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  RCS Flows
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage flow records for this client.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchFlows}>
                  Refresh
                </Button>
                <Button variant="contained" startIcon={<AddIcon />} onClick={openCreateFlowDialog}>
                  Add Flow
                </Button>
              </Stack>
            </Stack>

            {loadingFlows ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : flows.length === 0 ? (
              <Alert severity="info">No RCS flows found for this client.</Alert>
            ) : (
              <Grid container spacing={2.5}>
                {flows.map((flow) => (
                  <Grid item xs={12} md={6} lg={4} key={flow.id || flow.flow_id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                        border: "1px solid rgba(148, 163, 184, 0.16)",
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} noWrap>
                              {flow.flow_name || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Flow ID: {flow.flow_id || "-"}
                            </Typography>
                          </Box>
                          <Chip
                            label={flow.is_active ? "Active" : "Inactive"}
                            color={flow.is_active ? "success" : "default"}
                            size="small"
                          />
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" sx={{ mb: 1 }}>
                          <strong>Entry Point:</strong> {flow.entry_point || "-"}
                        </Typography>
                        <Typography variant="body2" sx={{ mb: 2 }}>
                          <strong>Steps Count:</strong>{" "}
                          {flow.steps ? Object.keys(flow.steps).length : 0}
                        </Typography>

                        <TextField
                          fullWidth
                          multiline
                          minRows={6}
                          label="Steps Preview"
                          value={formatJson(flow.steps, {})}
                          InputProps={{ readOnly: true }}
                        />

                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openEditFlowDialog(flow)}
                          >
                            Edit
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteFlow(flow.flow_id)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      {tab === 2 && (
        <Card
          sx={{
            borderRadius: 3,
            boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
            border: "1px solid rgba(148, 163, 184, 0.16)",
          }}
        >
          <CardContent>
            <Stack
              direction={{ xs: "column", md: "row" }}
              justifyContent="space-between"
              alignItems={{ xs: "flex-start", md: "center" }}
              spacing={2}
              sx={{ mb: 3 }}
            >
              <Box>
                <Typography variant="h6" fontWeight={700}>
                  RCS Custom Messages
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Manage offer, event, and custom message templates.
                </Typography>
              </Box>

              <Stack direction="row" spacing={1}>
                <Button variant="outlined" startIcon={<RefreshIcon />} onClick={fetchMessages}>
                  Refresh
                </Button>
                <Button
                  variant="contained"
                  startIcon={<AddIcon />}
                  onClick={openCreateMessageDialog}
                >
                  Add Message
                </Button>
              </Stack>
            </Stack>

            {loadingMessages ? (
              <Box sx={{ py: 6, display: "flex", justifyContent: "center" }}>
                <CircularProgress />
              </Box>
            ) : messages.length === 0 ? (
              <Alert severity="info">No RCS custom messages found for this client.</Alert>
            ) : (
              <Grid container spacing={2.5}>
                {messages.map((item) => (
                  <Grid item xs={12} md={6} lg={4} key={item.id}>
                    <Card
                      sx={{
                        borderRadius: 3,
                        boxShadow: "0 6px 20px rgba(15, 23, 42, 0.06)",
                        border: "1px solid rgba(148, 163, 184, 0.16)",
                        height: "100%",
                      }}
                    >
                      <CardContent>
                        <Stack
                          direction="row"
                          justifyContent="space-between"
                          alignItems="flex-start"
                          spacing={1}
                        >
                          <Box sx={{ minWidth: 0 }}>
                            <Typography variant="h6" fontWeight={700} noWrap>
                              {item.title || "-"}
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                              Type: {item.message_type || "-"}
                            </Typography>
                          </Box>
                          <Chip
                            label={item.is_active ? "Active" : "Inactive"}
                            color={item.is_active ? "success" : "default"}
                            size="small"
                          />
                        </Stack>

                        <Divider sx={{ my: 2 }} />

                        <Typography variant="body2" sx={{ mb: 1.5 }}>
                          {item.body || "-"}
                        </Typography>

                        <Stack direction="row" spacing={1} flexWrap="wrap" sx={{ mb: 2 }}>
                          {(item.tags || []).map((tag, index) => (
                            <Chip
                              key={`${tag}-${index}`}
                              label={tag}
                              size="small"
                              variant="outlined"
                            />
                          ))}
                        </Stack>

                        <TextField
                          fullWidth
                          multiline
                          minRows={5}
                          label="Payload Preview"
                          value={formatJson(item.payload, {})}
                          InputProps={{ readOnly: true }}
                        />

                        <Stack direction="row" spacing={1} sx={{ mt: 2 }}>
                          <Button
                            fullWidth
                            variant="outlined"
                            startIcon={<EditIcon />}
                            onClick={() => openEditMessageDialog(item)}
                          >
                            Edit
                          </Button>
                          <Button
                            fullWidth
                            variant="outlined"
                            color="error"
                            startIcon={<DeleteIcon />}
                            onClick={() => deleteMessage(item.id)}
                          >
                            Delete
                          </Button>
                        </Stack>
                      </CardContent>
                    </Card>
                  </Grid>
                ))}
              </Grid>
            )}
          </CardContent>
        </Card>
      )}

      <Dialog
        open={flowDialogOpen}
        onClose={() => setFlowDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>{flowForm.id ? "Edit RCS Flow" : "Create RCS Flow"}</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Flow ID"
                value={flowForm.flow_id}
                onChange={(e) => setFlowForm((prev) => ({ ...prev, flow_id: e.target.value }))}
                error={Boolean(errors.flow.flow_id)}
                helperText={errors.flow.flow_id}
                disabled={Boolean(flowForm.id)}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Flow Name"
                value={flowForm.flow_name}
                onChange={(e) =>
                  setFlowForm((prev) => ({ ...prev, flow_name: e.target.value }))
                }
                error={Boolean(errors.flow.flow_name)}
                helperText={errors.flow.flow_name}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Entry Point"
                value={flowForm.entry_point}
                onChange={(e) =>
                  setFlowForm((prev) => ({ ...prev, entry_point: e.target.value }))
                }
                error={Boolean(errors.flow.entry_point)}
                helperText={errors.flow.entry_point}
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.5,
                  minHeight: 56,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={flowForm.is_active}
                      onChange={(e) =>
                        setFlowForm((prev) => ({ ...prev, is_active: e.target.checked }))
                      }
                    />
                  }
                  label={flowForm.is_active ? "Active" : "Inactive"}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={12}
                label="Steps (JSON)"
                value={flowForm.stepsText}
                onChange={(e) => setFlowForm((prev) => ({ ...prev, stepsText: e.target.value }))}
                error={Boolean(errors.flow.stepsText)}
                helperText={errors.flow.stepsText || "Enter valid JSON for steps."}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setFlowDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveFlow}
            disabled={savingFlow}
            startIcon={savingFlow ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />}
          >
            {flowForm.id ? "Update Flow" : "Create Flow"}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={messageDialogOpen}
        onClose={() => setMessageDialogOpen(false)}
        fullWidth
        maxWidth="md"
      >
        <DialogTitle>
          {messageForm.id ? "Edit Custom Message" : "Create Custom Message"}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2.5} sx={{ mt: 0.2 }}>
            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Title"
                value={messageForm.title}
                onChange={(e) => setMessageForm((prev) => ({ ...prev, title: e.target.value }))}
                error={Boolean(errors.message.title)}
                helperText={errors.message.title}
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                select
                fullWidth
                label="Message Type"
                value={messageForm.message_type}
                onChange={(e) =>
                  setMessageForm((prev) => ({ ...prev, message_type: e.target.value }))
                }
                error={Boolean(errors.message.message_type)}
                helperText={errors.message.message_type}
              >
                {messageTypeOptions.map((item) => (
                  <MenuItem key={item} value={item}>
                    {item}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>

            <Grid item xs={12} md={3}>
              <Paper
                variant="outlined"
                sx={{
                  px: 2,
                  py: 1.5,
                  minHeight: 56,
                  display: "flex",
                  alignItems: "center",
                  borderRadius: 2,
                }}
              >
                <FormControlLabel
                  control={
                    <Switch
                      checked={messageForm.is_active}
                      onChange={(e) =>
                        setMessageForm((prev) => ({ ...prev, is_active: e.target.checked }))
                      }
                    />
                  }
                  label={messageForm.is_active ? "Active" : "Inactive"}
                />
              </Paper>
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={4}
                label="Message Body"
                value={messageForm.body}
                onChange={(e) => setMessageForm((prev) => ({ ...prev, body: e.target.value }))}
                error={Boolean(errors.message.body)}
                helperText={errors.message.body}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Tags"
                value={messageForm.tagsText}
                onChange={(e) =>
                  setMessageForm((prev) => ({ ...prev, tagsText: e.target.value }))
                }
                helperText="Comma separated. Example: promo, summer, launch"
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={10}
                label="Payload (JSON)"
                value={messageForm.payloadText}
                onChange={(e) =>
                  setMessageForm((prev) => ({ ...prev, payloadText: e.target.value }))
                }
                error={Boolean(errors.message.payloadText)}
                helperText={errors.message.payloadText || "Enter valid JSON payload."}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setMessageDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveMessage}
            disabled={savingMessage}
            startIcon={
              savingMessage ? <CircularProgress size={18} color="inherit" /> : <SaveIcon />
            }
          >
            {messageForm.id ? "Update Message" : "Create Message"}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={closeSnackbar}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
      >
        <Alert onClose={closeSnackbar} severity={snackbar.severity} variant="filled">
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ClientRcsManagement;